/**
 * Bulk order grid: enter quantities for every variant of a product and add
 * them to the cart in a single request.
 *
 * The pure functions below are exported on window.BulkOrderGrid so they can be
 * tested with `node --test` without a browser or a build step.
 */
(function () {
  function normalizeQuantity(value) {
    var quantity = Number(value);
    if (!isFinite(quantity) || quantity <= 0) return 0;
    return Math.floor(quantity);
  }

  /**
   * @param {Array<{variantId:number,quantity:number,price:number}>} rows
   * @returns {{pieces:number,cents:number}}
   */
  function computeTotals(rows) {
    return rows.reduce(
      function (acc, row) {
        var quantity = normalizeQuantity(row.quantity);
        acc.pieces += quantity;
        acc.cents += quantity * row.price;
        return acc;
      },
      { pieces: 0, cents: 0 }
    );
  }

  /**
   * The minimum applies to the whole cart for this product, not to a single
   * add, so quantities already in the cart count toward it.
   *
   * @param {number} pieces  entered in the grid right now
   * @param {number} inCart  already in the cart for this product
   * @param {{min:number,max:number|null}|null} limit
   * @returns {{enabled:boolean,reason:string,remaining:number,over:number}}
   */
  function gateState(pieces, inCart, limit) {
    var total = pieces + inCart;
    var min = limit && limit.min ? limit.min : 0;
    var remaining = Math.max(0, min - total);

    if (pieces <= 0) {
      return { enabled: false, reason: 'empty', remaining: remaining, over: 0 };
    }
    if (!limit) {
      return { enabled: true, reason: 'ungated', remaining: 0, over: 0 };
    }
    if (min && total < min) {
      return { enabled: false, reason: 'below', remaining: remaining, over: 0 };
    }
    if (limit.max && total > limit.max) {
      return { enabled: false, reason: 'above', remaining: 0, over: total - limit.max };
    }
    return { enabled: true, reason: 'ok', remaining: 0, over: 0 };
  }

  /**
   * @param {Array<{variantId:number,quantity:number}>} rows
   * @returns {Array<{id:number,quantity:number}>}
   */
  function buildCartItems(rows) {
    return rows
      .map(function (row) {
        return { id: row.variantId, quantity: normalizeQuantity(row.quantity) };
      })
      .filter(function (item) {
        return item.quantity > 0;
      });
  }

  /**
   * The order minimum applies to the cart, not to a single add. Someone
   * returning with 6 already in the cart needs 6 more, not 12.
   *
   * @param {{items:Array<{id:number,quantity:number}>}|null} cart
   * @param {Array<number>} variantIds
   * @returns {number}
   */
  function countInCart(cart, variantIds) {
    if (!cart || !Array.isArray(cart.items)) return 0;
    return cart.items.reduce(function (sum, item) {
      return variantIds.indexOf(Number(item.id)) === -1 ? sum : sum + Number(item.quantity);
    }, 0);
  }

  window.BulkOrderGrid = {
    formatMoney: formatMoney,
    computeTotals: computeTotals,
    gateState: gateState,
    buildCartItems: buildCartItems,
    countInCart: countInCart,
  };

  /**
   * Shopify's money format is injected as window.BulkOrderGridMoneyFormat by the
   * snippet. It names one of several amount placeholders, each with its own
   * grouping and decimal convention — substituting only `{{amount}}` left the
   * literal placeholder in place for shops using any of the others, and
   * toFixed(2) alone produced "$1695.00" beside Liquid's "$1,695.00".
   */
  function group(value, thousands, decimal, decimals) {
    var parts = (cents0(value, decimals)).split('.');
    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
    return parts.length > 1 ? parts.join(decimal) : parts[0];
  }

  function cents0(cents, decimals) {
    return (cents / 100).toFixed(decimals);
  }

  var MONEY_PLACEHOLDERS = {
    amount: function (c) { return group(c, ',', '.', 2); },
    amount_no_decimals: function (c) { return group(c, ',', '.', 0); },
    amount_with_comma_separator: function (c) { return group(c, '.', ',', 2); },
    amount_no_decimals_with_comma_separator: function (c) { return group(c, '.', ',', 0); },
    amount_with_apostrophe_separator: function (c) { return group(c, "'", '.', 2); },
  };

  function formatMoney(cents) {
    var format = window.BulkOrderGridMoneyFormat;
    // No format published: return a bare grouped decimal rather than invent a
    // currency symbol.
    if (!format) return group(cents, ',', '.', 2);

    return format.replace(/\{\{\s*(\w+)\s*\}\}/g, function (match, name) {
      var render = MONEY_PLACEHOLDERS[name];
      return render ? render(cents) : match;
    });
  }

  function t(key, replacements) {
    var strings = (window.bulkGridStrings || {});
    var template = strings[key] || '';
    Object.keys(replacements || {}).forEach(function (name) {
      template = template.replace('{{ ' + name + ' }}', replacements[name]);
    });
    return template;
  }

  if (!customElements.get('bulk-order-grid')) {
    customElements.define(
      'bulk-order-grid',
      class BulkOrderGrid extends HTMLElement {
        connectedCallback() {
          this.productId = Number(this.dataset.productId);
          this.inCart = 0;
          this.limit = window.BulkOrderLimits
            ? window.BulkOrderLimits.getMinimumForProduct(this.productId)
            : null;

          this.submit = this.querySelector('[data-bulk-submit]');
          this.submitLabel = this.querySelector('[data-bulk-submit-label]');
          this.spinner = this.querySelector('.loading-overlay__spinner');
          this.errorEl = this.querySelector('[data-bulk-error]');
          this.meterEl = this.querySelector('[data-bulk-meter]');
          this.totalEl = this.querySelector('[data-bulk-total]');
          this.stateEl = this.querySelector('[data-bulk-state]');
          this.fillEl = this.querySelector('[data-bulk-fill]');
          this.subtotalEl = this.querySelector('[data-bulk-subtotal]');
          this.clearEl = this.querySelector('[data-bulk-clear]');

          this.renderNotice();
          this.bindSteppers();
          this.bindSwatches();

          this.matrix = this.querySelector('[data-bulk-matrix-scroll]');
          this.accordion = this.querySelector('[data-bulk-accordion]');
          this.chooseLayout();
          this.bindInputs();
          this.bindAccordion();
          if (this.matrix && this.accordion) {
            this.observer = new ResizeObserver(this.chooseLayout.bind(this));
            this.observer.observe(this);
            // Fonts load after this script runs; the metrics they change are
            // exactly what the layout decision measures.
            if (document.fonts && document.fonts.ready) {
              document.fonts.ready.then(this.chooseLayout.bind(this));
            }
          }

          this.submit.addEventListener('click', this.onSubmit.bind(this));
          if (this.clearEl) this.clearEl.addEventListener('click', this.onClear.bind(this));
          this.refresh();
          this.loadCartQuantities();
        }

        /**
         * The order minimum applies to the cart, not to a single add, so a
         * customer returning with some already in the cart needs to see
         * that reflected in the meter. Fetches /cart.js and seeds `inCart`.
         * Fails soft: an unreachable or unparseable cart leaves `inCart` at
         * 0 and the grid fully usable — a cart-fetch failure must never
         * break the page.
         */
        loadCartQuantities() {
          // Sold-out variants render no input but can still hold cart quantity,
          // so use the full list the snippet emits; fall back to the rendered
          // inputs only if the attribute is missing.
          var declared = (this.dataset.variantIds || '')
            .split(',')
            .map(Number)
            .filter(function (id) { return id > 0; });
          var variantIds = declared.length
            ? declared
            : this.inputs.map(function (input) { return Number(input.dataset.variantId); });

          fetch(window.routes.cart_url + '.js')
            .then(function (response) { return response.json(); })
            .then(
              function (cart) {
                this.inCart = window.BulkOrderGrid.countInCart(cart, variantIds);
                // The in-cart count is surfaced by refresh() on the meter, not
                // on the notice: the notice is optional (show_notice) and is
                // switched off wherever the order-limits app renders its own
                // banner, but the meter is always present when a limit exists.
                this.refresh();
              }.bind(this)
            )
            .catch(function () {
              /* Cart unreadable: leave inCart at 0 and stay usable. */
            });
        }

        /**
         * The matrix is the better layout when it fits. Rather than guess a
         * breakpoint — the info column is 553px at a 1200px viewport but only
         * 456px at 1024px, and a 5-size run fits where a 7-size run does not —
         * render it and measure whether it overflows.
         */
        chooseLayout() {
          if (!this.matrix || !this.accordion) return;

          // Measuring means writing data-layout then reading scrollWidth, which
          // forces synchronous layout — and this element is the ResizeObserver's
          // own target, so an unguarded re-measure can retrigger the observer off
          // its own write. Only the container's width can change the outcome, so
          // skip when it has not moved.
          var table = this.matrix.querySelector('table');
          var width = this.getBoundingClientRect().width;
          // Guard on the table's intrinsic width as well as the container's: a
          // web font swapping in after first paint widens the header row without
          // the container changing at all, which would otherwise lock in a
          // matrix that no longer fits.
          var intrinsic = table ? table.scrollWidth : 0;
          if (this.lastMeasuredWidth === width && this.lastIntrinsicWidth === intrinsic) return;
          this.lastMeasuredWidth = width;
          this.lastIntrinsicWidth = intrinsic;

          var previous = this.dataset.layout;
          this.dataset.layout = 'matrix';
          var table = this.matrix.querySelector('table');
          var overflows = table.scrollWidth > this.matrix.clientWidth;
          this.dataset.layout = overflows ? 'accordion' : 'matrix';
          if (this.dataset.layout !== previous && this.inputs) this.refresh();
        }

        bindAccordion() {
          Array.prototype.slice
            .call(this.querySelectorAll('[data-bulk-acc]'))
            .forEach(
              function (head) {
                head.addEventListener('click', function () {
                  var open = head.getAttribute('aria-expanded') === 'true';
                  head.setAttribute('aria-expanded', String(!open));
                  var body = head.parentElement.querySelector(
                    '[data-bulk-acc-body="' + head.dataset.bulkAcc + '"]'
                  );
                  if (body) body.hidden = open;
                });
              }.bind(this)
            );
        }

        /** Inputs belonging to the layout that is currently on screen. */
        visibleScope() {
          if (this.dataset.layout === 'accordion') return this.accordion;
          if (this.dataset.layout === 'matrix') return this.matrix;
          return this;
        }

        renderNotice() {
          var notice = this.querySelector('[data-bulk-notice]');
          if (!notice || !this.limit || !this.limit.message) return;
          notice.textContent = this.limit.message;
          notice.hidden = false;
        }

        /**
         * Colourway swatches drive Dawn's media gallery through the same
         * setActiveMedia call its variant picker makes. Dawn's implementation
         * assumes the media exists and would throw on a miss, so confirm the
         * target is present in that gallery before asking it to switch.
         */
        bindSwatches() {
          var strip = this.querySelector('[data-bulk-swatches]');
          if (!strip) return;

          strip.addEventListener(
            'click',
            function (event) {
              var button = event.target.closest('[data-bulk-swatch]');
              if (!button || !strip.contains(button)) return;

              Array.prototype.slice.call(strip.querySelectorAll('[data-bulk-swatch]')).forEach(
                function (other) {
                  var active = other === button;
                  other.classList.toggle('is-active', active);
                  other.setAttribute('aria-pressed', String(active));
                }
              );

              // Before the gallery, which returns early when a colourway has no
              // image — that must not skip opening the panel.
              this.revealColour(button.dataset.bulkSwatch);

              var mediaId = button.dataset.mediaId;
              if (!mediaId) return;
              Array.prototype.slice.call(document.querySelectorAll('[id^="MediaGallery-"]')).forEach(
                function (gallery) {
                  if (typeof gallery.setActiveMedia !== 'function') return;
                  if (!gallery.querySelector('[data-media-id="' + mediaId + '"]')) return;
                  gallery.setActiveMedia(mediaId, true);
                }
              );
            }.bind(this)
          );
        }

        /**
         * A swatch and an accordion header are two views of the same colourway,
         * so picking a swatch opens that colour and collapses the others —
         * otherwise clicking through the strip leaves every panel open. No-op in
         * the matrix and flat-list layouts, which have no panels.
         */
        revealColour(index) {
          if (index === undefined || !this.accordion) return;

          var opened = null;
          Array.prototype.slice.call(this.querySelectorAll('[data-bulk-acc]')).forEach(
            function (head) {
              var match = head.dataset.bulkAcc === index;
              head.setAttribute('aria-expanded', String(match));
              var body = this.querySelector('[data-bulk-acc-body="' + head.dataset.bulkAcc + '"]');
              if (body) body.hidden = !match;
              if (match) opened = head;
            }.bind(this)
          );

          // `nearest` leaves the page still when the panel is already on screen.
          if (opened && this.dataset.layout === 'accordion') {
            opened.scrollIntoView({ block: 'nearest' });
          }
        }

        /**
         * Stepper buttons are delegated rather than bound per button, so the
         * handler survives any future re-render of the rows.
         */
        bindSteppers() {
          this.addEventListener('click', function (event) {
            var button = event.target.closest('[data-bulk-step]');
            if (!button || !this.contains(button)) return;
            var input = button.parentElement.querySelector('input[data-variant-id]');
            if (!input) return;
            var current = parseInt(input.value, 10);
            if (!isFinite(current) || current < 0) current = 0;
            var next = current + parseInt(button.dataset.bulkStep, 10);
            input.value = String(Math.max(0, next));
            input.dispatchEvent(new Event('input', { bubbles: true }));
          }.bind(this));
        }

        /** Disable a row's minus button at zero so the control reads honestly. */
        syncSteppers() {
          Array.prototype.slice.call(this.querySelectorAll('.bulk-grid__stepper')).forEach(
            function (stepper) {
              var input = stepper.querySelector('input[data-variant-id]');
              var minus = stepper.querySelector('[data-bulk-step="-1"]');
              if (!input || !minus) return;
              var value = parseInt(input.value, 10);
              minus.disabled = !isFinite(value) || value <= 0;
            }
          );
        }

        bindInputs() {
          this.inputs = Array.prototype.slice.call(this.querySelectorAll('input[data-variant-id]'));
          this.inputs.forEach(
            function (input) {
              input.addEventListener(
                'input',
                function () {
                  this.inputs.forEach(function (other) {
                    if (other !== input && other.dataset.variantId === input.dataset.variantId) {
                      other.value = input.value;
                    }
                  });
                  this.refresh();
                }.bind(this)
              );
              input.addEventListener('focus', function () { input.select(); });
            }.bind(this)
          );
        }

        /**
         * Empty every quantity box and re-gate. The cart is deliberately left
         * alone: this clears what has been typed, not what has been bought, so
         * the meter keeps counting whatever `inCart` holds.
         *
         * Clears `this.inputs` rather than the visible layout's inputs. The
         * matrix and the accordion each render an input per variant and only
         * one is on screen, so clearing the visible set alone would leave
         * stale quantities in the hidden layout that reappear on resize.
         *
         * Writes '0', which is what the snippet renders into every box at rest.
         * Blanking them instead would leave the grid looking unlike a freshly
         * loaded one, since there is no placeholder behind the value.
         */
        onClear() {
          this.inputs.forEach(function (input) {
            input.value = '0';
          });
          this.refresh();
          // refresh() has just hidden the button that was focused, which would
          // otherwise strand a keyboard user on nothing. offsetParent screens
          // out both the off-screen layout's inputs and any sitting inside a
          // collapsed accordion section — neither can take focus.
          var first = Array.prototype.slice
            .call(this.visibleScope().querySelectorAll('input[data-variant-id]'))
            .filter(function (input) { return input.offsetParent !== null; })[0];
          if (first) first.focus();
        }

        /**
         * The matrix and the accordion render inputs for the same variants;
         * only one layout is visible at a time. Reading every input in the
         * element would double-count each quantity, so this reads only the
         * inputs belonging to the layout currently on screen.
         */
        rows() {
          var scope = this.visibleScope();
          return Array.prototype.slice
            .call(scope.querySelectorAll('input[data-variant-id]'))
            .map(function (input) {
              return {
                variantId: Number(input.dataset.variantId),
                price: Number(input.dataset.price),
                quantity: Number(input.value),
              };
            });
        }

        refresh() {
          var rows = this.rows();
          var totals = window.BulkOrderGrid.computeTotals(rows);
          var state = window.BulkOrderGrid.gateState(totals.pieces, this.inCart, this.limit);

          this.inputs.forEach(function (input) {
            var row = input.closest('[data-bulk-row]');
            if (row) row.classList.toggle('is-filled', Number(input.value) > 0);
          });

          var min = this.limit ? this.limit.min : 0;
          var counted = totals.pieces + this.inCart;

          if (this.meterEl) this.meterEl.hidden = !min;

          var progressText = min
            ? t('minimum_progress', { count: counted, min: min })
            : counted + '';
          // Without this a returning customer sees "12 of 12 minimum" having
          // entered nothing, with no explanation of where the 12 came from.
          if (this.inCart > 0) {
            progressText += ' · ' + t('in_cart', { count: this.inCart });
          }
          this.totalEl.textContent = progressText;

          var stateEl = this.stateEl;
          if (state.reason === 'above') {
            stateEl.textContent = t('over', { count: state.over });
          } else if (state.remaining > 0) {
            stateEl.textContent = t('remaining', { count: state.remaining });
          } else if (min) {
            stateEl.textContent = t('met', {});
          } else {
            stateEl.textContent = '';
          }

          var fillEl = this.fillEl;
          fillEl.style.width = min ? Math.min(100, (counted / min) * 100) + '%' : '0%';
          fillEl.classList.toggle('is-met', Boolean(min) && counted >= min);
          this.subtotalEl.textContent = formatMoney(totals.cents);
          // Nothing typed, nothing to clear. Driving this from refresh() also
          // gets the bfcache case free: a back-navigation restores the boxes
          // and reruns connectedCallback's refresh, so the button returns with
          // the quantities it belongs to.
          if (this.clearEl) this.clearEl.hidden = totals.pieces === 0;
          this.syncSteppers();

          this.submit.disabled = !state.enabled;
          // Write the label, not the button: the button also holds the spinner.
          this.submitLabel.textContent = totals.pieces
            ? t('add', { count: totals.pieces })
            : t('add_empty', {});

          // Both layouts render every variant, but only one is on screen; summing
          // the hidden one's rows updates counters nobody can see.
          if (this.dataset.layout === 'matrix') {
          Array.prototype.slice.call(this.querySelectorAll('[data-bulk-color-row]')).forEach(
            function (row) {
              var sum = 0;
              Array.prototype.slice.call(row.querySelectorAll('input')).forEach(function (input) {
                sum += Number(input.value) > 0 ? Math.floor(Number(input.value)) : 0;
              });
              row.querySelector('[data-bulk-color-total]').textContent = sum;
            }
          );

          }

          if (this.dataset.layout === 'accordion') {
          Array.prototype.slice.call(this.querySelectorAll('[data-bulk-acc-count]')).forEach(
            function (badge) {
              var body = this.querySelector('[data-bulk-acc-body="' + badge.dataset.bulkAccCount + '"]');
              var sum = 0;
              if (body) {
                Array.prototype.slice.call(body.querySelectorAll('input')).forEach(function (input) {
                  sum += Number(input.value) > 0 ? Math.floor(Number(input.value)) : 0;
                });
              }
              badge.textContent = sum;
            }.bind(this)
          );
          }
        }

        onSubmit() {
          var items = window.BulkOrderGrid.buildCartItems(this.rows());
          if (!items.length) return;

          this.errorEl.hidden = true;
          this.submit.disabled = true;
          this.submit.classList.add('loading');
          if (this.spinner) this.spinner.classList.remove('hidden');

          var config = fetchConfig('javascript');
          config.body = JSON.stringify({ items: items });

          fetch(window.routes.cart_add_url, config)
            .then(function (response) { return response.json(); })
            .then(
              function (body) {
                if (body.status) throw new Error(body.description || body.message);
                window.location = window.routes.cart_url;
              }.bind(this)
            )
            .catch(
              function (error) {
                // Prefer the server's explanation ("below the minimum", "sold
                // out") over the generic retry message; without it a customer
                // retries an add that can never succeed.
                this.errorEl.textContent = (error && error.message) || t('error', {});
                this.errorEl.hidden = false;
                this.submit.classList.remove('loading');
                if (this.spinner) this.spinner.classList.add('hidden');
                this.refresh();
              }.bind(this)
            );
        }
      }
    );
  }
})();
